/**
 * Die Beschriftungen und Farben, mit denen ein Befund überall gleich aussieht.
 *
 * Sie stehen an einer Stelle, weil dieselben Wörter in der Gegenüberstellung, in
 * der Befundleiste und in der Bestandsansicht erscheinen — dieselbe Beschriftung
 * an drei Orten wären drei Wahrheiten, sobald eine davon nachgezogen wird und die
 * anderen nicht.
 *
 * Die Werte der geschlossenen Mengen bleiben deutsch, weil das Glossar sie
 * festlegt und sie so in der Datenbank stehen; die Schlüssel sind die Kennungen
 * der API.
 *
 * Daneben steht hier, was eine Ansicht aus den beiden Achsen einer **Stelle**
 * abliest — welcher Handgriff gesetzt ist, wer ihn wann gesetzt hat, ob es
 * etwas zurückzunehmen gibt. Aus demselben Grund: Der Befundverlauf und die
 * Gegenüberstellung stellen dieselbe Frage, und zwei Rechnungen dafür wären
 * zwei Antworten, sobald eine nachgezogen wird.
 */

import type {
  Finding,
  FindingStatus,
  Qualification,
  RefutationResult,
  Severity,
  SiteAuthor,
  Verdict,
} from "../api/client";
import type { StatusPillProps } from "../design-system";
import { formatTimestamp } from "../formatting";

/**
 * Eine der acht Farben, die `core/StatusPill` kennt.
 *
 * Die Konstanten darunter bilden seit **#141** darauf ab und nicht mehr auf die
 * Bedeutungen `info`, `success`, `warning`, `danger` des abgelösten `Badge`: Der
 * V3-Baukasten benennt Farben, die Bedeutung gehört dem Produkt — und sie steht
 * im Namen der Konstante, nicht im Wert.
 *
 * **`null` ist keine Farbe, sondern die andere Hälfte der Regel.** `StatusPill`
 * kennt kein `neutral`, und das ist Absicht: Was farblos bleibt, ist kein
 * Zustand, sondern ein Etikett, und dafür steht `core/Chip`. Ein Aufrufort, der
 * `null` bekommt, zeichnet also einen `Chip` — die Typprüfung lässt ihm keine
 * Wahl, weil `StatusPill` keinen leeren Ton annimmt. Ihn stattdessen zu färben
 * hieße, einem Befund eine Aussage zu geben, die er nie hatte.
 */
export type Tone = NonNullable<StatusPillProps["tone"]>;

/**
 * Das Zeichen, mit dem ein **Kontrollmuster** unter den Urhebern erscheint.
 *
 * Ein Muster steht **nicht** im Namensband: Es ist keine Meinung, sondern eine
 * Regel, die immer anschlägt (ADR-0010) — und „Zuweisung in einer Bedingung"
 * läse sich zwischen Agentennamen wie ein weiterer Agent. Es steht als eigenes
 * Zeichen daneben, einmal, gleich wie viele Muster angeschlagen haben: Die
 * Auskunft lautet „hier war auch eine Regel beteiligt" und nicht „welche".
 * Welche es waren, steht im Titel.
 */
export const RULE = "Regel";

/** Was an der Stelle des Titels steht, wo es keinen gibt. */
export const UNTITLED_LABEL = "ohne Titel";

/**
 * Warum eine Zeile ohne Titel dasteht — und warum keiner erfunden wird.
 *
 * An einer Stelle, weil beide Listen dieselbe Lücke zeigen: Der Befundvorrat und
 * der Revisionsstrom lesen denselben Titel, und wo er fehlt, fehlt er aus
 * demselben Grund.
 */
export const UNTITLED =
  "Die Befunde an dieser Stelle stammen aus einem Lauf, bevor der Titel Pflicht wurde. " +
  "Ein aus dem Befundtext geschnittener erster Satz stünde hier für eine Fassung, die " +
  "niemand geschrieben hat.";

/**
 * Die Namen der Urheber, wie sie in einer Liste stehen — Agenten und Menschen,
 * kommagetrennt.
 *
 * Kontrollmuster sind **nicht** dabei; für sie steht das Zeichen `RULE`.
 *
 * An einer Stelle, weil der Revisionsstrom und der Befundvorrat dieselben
 * Urheber nennen — zwei Schreibweisen wären zwei Wahrheiten über denselben Fund.
 */
export function labelOfAuthors(authors: SiteAuthor[]): string {
  return authors
    .filter((one) => one.origin !== "pattern")
    .map((one) => one.name)
    .join(", ");
}

/** Ob unter den Urhebern ein Kontrollmuster ist — dann steht „Regel" daneben. */
export function carriesRule(authors: SiteAuthor[]): boolean {
  return authors.some((one) => one.origin === "pattern");
}

/** Die Beschriftungen der beteiligten Kontrollmuster — im Titel, nicht in der Zeile. */
export function labelOfRules(authors: SiteAuthor[]): string {
  return authors
    .filter((one) => one.origin === "pattern")
    .map((one) => one.name)
    .join(", ");
}

/** Wie schwer ein Befund wiegt, in der Farbe des Systems. Rot heißt „muss". */
export const SEVERITY_TONES: Record<Severity, Tone | null> = {
  hinweis: null,
  mittel: "amber",
  schwer: "red",
};

/**
 * Der **Lebenszyklus**, deutsch beschriftet.
 *
 * **Die drei Ausgänge tragen verschiedene Namen**, und das ist keine Feinheit:
 * „behoben" setzt ein Mensch und sagt, dass der Befund zutraf; „verworfen" sagt
 * nur, dass hier nicht weitergearbeitet wird; „überschrieben" setzt das System
 * und sagt nur, dass es die Stelle so nicht mehr gibt. Ein gemeinsames Wort —
 * „erledigt" — machte den Beleg unlesbar, für den „behoben" überhaupt erhoben
 * wird (ADR-0013).
 */
export const STATUS_LABELS: Record<FindingStatus, string> = {
  open: "offen",
  fixed: "behoben",
  dismissed: "verworfen",
  superseded: "überschrieben",
};

/**
 * Die drei Ausgänge, mit denen ein Befund den **Vorrat verlassen** hat.
 *
 * `dismissed` steht hier von Anfang an dabei, obwohl die Oberfläche ihn noch
 * nicht setzt: Seit Qualifizierung und Lebenszyklus getrennte Spalten sind
 * (ADR-0017), kann er im Bestand stehen — und eine Ansicht, die ihn nicht als
 * Ausgang läse, böte an einer verworfenen Stelle weiter „behoben" an.
 *
 * An einer Stelle für beide Ansichten: Der Befundvorrat und die Befundleiste
 * beantworten dieselbe Frage — steht hier noch Arbeit —, und zwei Aufzählungen
 * wären zwei Antworten darauf, sobald eine davon nachgezogen wird.
 */
export const GONE: FindingStatus[] = ["fixed", "dismissed", "superseded"];

/**
 * Die **Qualifizierung**, deutsch beschriftet.
 *
 * `rejected` heißt „Fehlalarm" und nicht „verworfen": Das Wort wird für den
 * Lebenszyklus gebraucht, und „Fehlalarm" ist ohnehin der Begriff, mit dem der
 * Quelltext diesen Wert seit jeher erklärt (Spec v1.4, „Beschriftungen"). Die
 * Werte bleiben englisch (ADR-0009).
 */
export const QUALIFICATION_LABELS: Record<Qualification, string> = {
  confirmed: "bestätigt",
  rejected: "Fehlalarm",
  acknowledged: "zutreffend, unerheblich",
};

/**
 * Die **Unterschrift unter dem Urteil**: wer eine Stelle wann bewertet hat.
 *
 * Die Bewertung ist ein Wert je Befund für alle und nicht einer je Person — ein
 * globales Urteil, das niemand unterschreibt, kann niemand hinterfragen
 * (ADR-0022). Sie steht deshalb überall dort neben der Qualifizierung, wo diese
 * zu sehen ist: in der Zeile des Vorrats, in der Befundleiste und in der
 * Gegenüberstellung.
 *
 * `null` heißt **es gibt kein Urteil** und nicht „von unbekannt": Der
 * Altbestand trägt weder Urheber noch Zeitpunkt, weil beides nie erhoben wurde,
 * und beides zu erfinden wäre schlimmer, als es zu vermissen. Fehlt allein der
 * Zeitpunkt, steht der Name für sich — eine halbe Angabe ist mehr als keine.
 */
export function labelOfJudgement(qualifiedBy: string, qualifiedAt: string | null): string | null {
  if (qualifiedBy === "") {
    return null;
  }

  return qualifiedAt === null
    ? `Bewertet von ${qualifiedBy}`
    : `Bewertet von ${qualifiedBy} am ${formatTimestamp(qualifiedAt)}`;
}

/**
 * Die **vier Handgriffe**, in der Reihenfolge, in der sie stehen (ADR-0022;
 * Spec v1.4, „Die vier Handgriffe").
 *
 * Von links nach rechts wächst, was der Handgriff über die Maschine sagt: „Fehlalarm"
 * spricht gegen sie, „zutreffend, nicht verfolgt" sagt über sie nichts,
 * „bestätigt" spricht für sie, „behoben" am stärksten. Wer die Reihe von links
 * liest, liest sie in genau dieser Steigerung.
 */
export const VERDICTS: Verdict[] = ["false-alarm", "not-pursued", "to-fix", "fixed"];

/**
 * Die Beschriftungen der Handgriffe. **Sie sagen, was der Handgriff behauptet**, und
 * nicht, welches Feld er schreibt: „Bestätigt, wird behoben" ist ein Satz über
 * die Stelle, „confirmed + open" wäre einer über den Bestand.
 *
 * „Zutreffend, nicht verfolgt" heißt so und nicht „unerheblich": Der Handgriff
 * behauptet ausdrücklich **nichts** über die Maschine, außer dass sie nicht
 * falsch lag — und das ist der Ausgang, der dem Vorrat bisher fehlte.
 */
export const VERDICT_LABELS: Record<Verdict, string> = {
  "false-alarm": "Fehlalarm",
  "not-pursued": "Zutreffend, nicht verfolgt",
  "to-fix": "Bestätigt, wird behoben",
  fixed: "Behoben",
};

/** Das Zeichen am Knopf. Vier verschiedene, weil vier verschiedene Aussagen. */
export const VERDICT_GLYPHS: Record<Verdict, string> = {
  "false-alarm": "x",
  "not-pursued": "minus",
  "to-fix": "wrench",
  fixed: "check",
};

/**
 * Was jeder Handgriff **festhält** — im Titel, weil er eine Aussage ist und
 * keine Schaltfläche.
 *
 * Jeder Satz sagt zweierlei: was über die Stelle gesagt wird und was daraus für
 * den Vorrat und die Abweichungskennzahl folgt. Wer „Fehlalarm" klickt, soll
 * wissen, dass er der Maschine widerspricht — und wer „behoben" klickt, dass er
 * ihr recht gibt.
 */
export const VERDICT_TITLES: Record<Verdict, string> = {
  "false-alarm":
    "Die Maschine hat sich geirrt: An diesen Zeilen ist nichts. Die Stelle verlässt den "
    + "Vorrat und zählt in der Abweichungskennzahl als Fehlalarm.",
  "not-pursued":
    "Die Beobachtung stimmt, eine Nachbesserung folgt daraus nicht. Über die Maschine sagt "
    + "das nichts, außer dass sie nicht falsch lag — die Stelle verlässt den Vorrat und "
    + "zählt in keiner der beiden Zahlen mit.",
  "to-fix":
    "Der Befund trifft zu, und die Stelle bleibt Arbeit. Sie steht weiter im Vorrat und "
    + "zählt als Treffer.",
  fixed:
    "Die Stelle ist in Ordnung gebracht — und damit ist gesagt, dass der Befund zutraf. Sie "
    + "verlässt den Vorrat und zählt als Treffer.",
};

/** Was die Rücknahme tut: den Zustand vor jedem Urteil wiederherstellen. */
export const WITHDRAW_VERDICT_TITLE =
  "Die Bewertung dieser Stelle zurücknehmen. Sie steht danach wieder offen und unbeurteilt "
  + "da — auch die eines Kollegen, denn ein falsches Urteil soll niemand festhalten, bis "
  + "sein Urheber aus dem Urlaub zurück ist.";

/**
 * Welcher Handgriff an einem Befund gesetzt ist — `null`, wenn keiner die
 * beiden Achsen so stehen ließ.
 *
 * Der Weg zurück durch die Tabelle aus ADR-0022. Er ist nötig, weil gespeichert
 * weiter je Befund und je Achse wird: Was gesetzt wurde, steht nirgends als
 * Handgriff da, sondern nur in seiner Wirkung. `null` steht deshalb auch für
 * Zustände, die kein Handgriff erzeugt — den Altbestand aus der Zeit vor
 * ADR-0022 und das stille `superseded` des Pollers.
 */
export function verdictOfFinding(
  status: FindingStatus,
  qualification: Qualification | null,
): Verdict | null {
  if (qualification === "rejected" && status === "dismissed") {
    return "false-alarm";
  }

  if (qualification === "acknowledged" && status === "dismissed") {
    return "not-pursued";
  }

  if (qualification === "confirmed" && status === "open") {
    return "to-fix";
  }

  return qualification === "confirmed" && status === "fixed" ? "fixed" : null;
}

/**
 * Der Zeitpunkt als Zahl. Ohne Zeitpunkt `NaN` und nicht 0: „nicht bekannt" ist
 * kein früher Zeitpunkt, und wer danach sortiert, entscheidet den Fall selbst.
 */
export function timeOf(iso: string | null): number {
  return iso === null ? Number.NaN : Date.parse(iso);
}

/**
 * Welcher **Handgriff** an einer Stelle gesetzt ist — `null`, wenn keiner
 * gesetzt ist oder die Befunde darunter verschieden stehen.
 *
 * Übergeben werden die Befunde **eines Ankers**, nicht die einer Ansicht:
 * Gesetzt wird nur an der Stelle, gespeichert weiter je Befund (ADR-0022). Eine
 * gemischte Stelle entsteht deshalb nur noch dadurch, dass **nach** der
 * Bewertung ein weiterer Befund an denselben Zeilen auftaucht — und der ist dann
 * tatsächlich noch nicht beurteilt. Einen Handgriff als gesetzt zu zeigen
 * behauptete etwas über ihn mit.
 *
 * Die Frage stellen zwei Flächen — der Befundverlauf und die Gegenüberstellung
 * —, und sie beantworten sie deshalb hier und nicht jede für sich.
 */
export function verdictOfSite(found: Finding[]): Verdict | null {
  let agreed: Verdict | null = null;

  for (const [at, one] of found.entries()) {
    const verdict = verdictOfFinding(one.status, one.qualification);

    if (verdict === null || (at > 0 && verdict !== agreed)) {
      return null;
    }

    agreed = verdict;
  }

  return agreed;
}

/**
 * Die **Unterschrift unter dem Urteil** einer Stelle: die jüngste, die an einem
 * ihrer Befunde steht.
 *
 * Die jüngste, weil das jüngste Urteil gilt (ADR-0022). `null` heißt: Hier hat
 * noch niemand geurteilt — nicht „von unbekannt".
 */
export function judgementOfSite(found: Finding[]): string | null {
  let latest: Finding | null = null;

  for (const one of found) {
    if (one.qualifiedBy === "") {
      continue;
    }

    if (latest === null || timeOf(one.qualifiedAt) > timeOf(latest.qualifiedAt)) {
      latest = one;
    }
  }

  return latest === null ? null : labelOfJudgement(latest.qualifiedBy, latest.qualifiedAt);
}

/**
 * Ob an dieser Stelle **überhaupt etwas gesetzt ist** — und damit, ob es etwas
 * zurückzunehmen gibt.
 *
 * Weiter gefasst als `verdictOfSite()`, und das mit Absicht: Ein Stand, den kein
 * Handgriff benennt, kommt vor — im Altbestand, den die Migration auf die
 * Qualifizierungsachse gehoben und im Lebenszyklus auf `open` gelassen hat
 * (ADR-0022), und an einer Stelle, an der nach der Bewertung ein weiterer Befund
 * aufgetaucht ist. Gäbe es die Rücknahme nur zum gesetzten Handgriff, wäre
 * ausgerechnet der Altbestand der einzige Stand, aus dem kein Weg zurück nach
 * „unbeurteilt" führte.
 *
 * `superseded` zählt nicht mit: Die Rücknahme fasst es nicht an, und ein Knopf,
 * der an einer solchen Stelle nichts täte, behauptete etwas anderes.
 */
export function judgedSite(found: Finding[]): boolean {
  return found.some(
    (one) => one.status !== "superseded" && (one.qualification !== null || one.status !== "open"),
  );
}

/**
 * Ob an dieser Stelle nur noch **überschriebene** Befunde liegen.
 *
 * Dann steht kein Handgriff da: Der Server nimmt an einem `superseded` nichts
 * mehr an, und ein Knopf, der nichts bewirkte, behauptete, ein Mensch könnte
 * hier noch etwas sagen. Liegt daneben auch nur ein Befund, der es nicht ist,
 * steht der Handgriff — er greift dann an dem.
 */
export function supersededSite(found: Finding[]): boolean {
  return found.every((one) => one.status === "superseded");
}

/**
 * Wie ein Widerlegungsversuch heißt. **„Widerlegt" ist kein Status des
 * Befundes**, sondern das Ergebnis eines zweiten Agenten daneben: Der Befund
 * bleibt stehen, unverändert und auffindbar, und nur seine Lautstärke ändert
 * sich.
 *
 * „unklar" steht ausdrücklich neben „gehalten" und fällt nicht mit ihm zusammen:
 * Der Auftrag lautet zu widerlegen, und im Zweifel gilt der Befund als nicht
 * gehalten.
 */
export const REFUTATION_LABELS: Record<RefutationResult, string> = {
  upheld: "gehalten",
  refuted: "widerlegt",
  unclear: "unklar",
};

export const REFUTATION_TONES: Record<RefutationResult, Tone | null> = {
  // Grün heißt hier: Der Versuch, ihn loszuwerden, ist gescheitert — der Befund
  // hat sich behauptet.
  upheld: "green",
  // Blau und nicht rot: „widerlegt" ist eine Auskunft und kein Alarm. Rot und
  // Orange gehören dem Schweregrad, und ein widerlegter Befund, der lauter
  // aussähe als ein bestätigter, stellte die Aussage auf den Kopf.
  refuted: "blue",
  unclear: null,
};

export const STATUS_TONES: Record<FindingStatus, Tone | null> = {
  // Farblos und nicht getönt: „offen", „verworfen" und „überschrieben" sind keine
  // Aussage über den Befund, und eine Farbe daneben läse sich wie eine. Sie
  // stehen deshalb als `Chip` da und nicht als `StatusPill`.
  open: null,
  fixed: "blue",
  dismissed: null,
  superseded: null,
};

export const QUALIFICATION_TONES: Record<Qualification, Tone | null> = {
  confirmed: "green",
  rejected: "red",
  acknowledged: null,
};
